import {
  Controller,
  FormProvider,
  UseFormReturn,
  useWatch,
} from "react-hook-form";
import { ControlledTextField } from "@benefit-systems/hr-tools-ui";
import __ from "src/utils/Translations";
import { Box, Skeleton, Stack, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import DashboardQueries from "src/Pages/Company/Dashboard/services/api/queryOptions";
import { useCompanyId } from "src/Pages/Company/CompanyContext";
import {
  useState,
  useEffect,
  useMemo,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
import debounce from "lodash/debounce";
import { dynamicNotification } from "src/utils/Notifications";
import IconActionMenu from "src/Pages/Company/Dashboard/components/IconActionMenu";
import DefaultIconWithPaper from "src/Pages/Company/Dashboard/pages/Widgets/shared/DefaultIconWithPaper";
import IconCropper from "src/Pages/Company/Dashboard/pages/Widgets/shared/IconCropper";
import useIconCropper from "src/Pages/Company/Dashboard/pages/Widgets/shared/useIconCropper";
import { NewLinkFormModels } from "./NewLinkForm.models";

export interface NewLinkFormRef {
  submitCrop: () => Promise<void>;
  isCropping: () => boolean;
}

interface NewLinkFormProps {
  formMethods: UseFormReturn<NewLinkFormModels>;
  onCroppingChange?: (isCropping: boolean) => void;
}

const NewLinkForm = forwardRef<NewLinkFormRef, NewLinkFormProps>(
  ({ formMethods, onCroppingChange }, ref) => {
    const iconValue = useWatch({ control: formMethods.control, name: "icon" });
    const urlValue = useWatch({ control: formMethods.control, name: "url" });
    const [debouncedUrl, setDebouncedUrl] = useState(urlValue);
    const [iconManuallySet, setIconManuallySet] = useState(false);
    const companyId = useCompanyId();

    const handleUploadSuccess = useCallback(
      (data: { id: string; icon: { original: string } | null }) => {
        if (data.icon) {
          formMethods.setValue("icon", data.icon, { shouldDirty: true });
          formMethods.setValue("iconId", data.id, { shouldDirty: true });
          setIconManuallySet(true);
        }
      },
      [formMethods]
    );

    const {
      imgLoaded,
      setImgLoaded,
      cropperImageSrc,
      isUploading,
      fileInputRef,
      iconCropperRef,
      uploadError,
      handleFileUpload,
      handleCropped,
      handleCancelCrop,
      isCropping,
      submitCrop,
    } = useIconCropper({
      iconValue,
      onCroppingChange,
      onUploadSuccess: handleUploadSuccess,
    });

    useImperativeHandle(ref, () => ({
      isCropping,
      submitCrop,
    }));

    const debouncedSetUrl = useMemo(() => debounce(setDebouncedUrl, 1000), []);

    useEffect(() => {
      debouncedSetUrl(urlValue);
      return () => debouncedSetUrl.cancel();
    }, [urlValue, debouncedSetUrl]);

    const [urlValid, setUrlValid] = useState(false);

    useEffect(() => {
      setUrlValid(false);
      if (!iconManuallySet) {
        formMethods.setValue("icon", null);
        formMethods.setValue("iconId", null);
      }
      if (debouncedUrl) {
        formMethods.trigger("url").then(setUrlValid);
      }
    }, [debouncedUrl, formMethods, iconManuallySet]);

    const {
      data,
      isLoading: isQueryLoading,
      isError,
    } = useQuery(
      DashboardQueries.getDashboardModuleCompanyRedirectsWidgetNewIconFromWebsite.companyId(
        !!debouncedUrl && urlValid && !iconManuallySet,
        companyId,
        { websiteUrl: debouncedUrl }
      )
    );

    const isLoading = isQueryLoading || isUploading;

    useEffect(() => {
      if (data?.icon && !iconManuallySet) {
        formMethods.setValue("icon", data.icon);
        formMethods.setValue("iconId", data.id);
      }
    }, [data, formMethods, iconManuallySet]);

    useEffect(() => {
      if (isError) {
        dynamicNotification(__("Nie udało się pobrać ikony."), "error");
      }
    }, [isError]);

    return (
      <FormProvider {...formMethods}>
        <Controller
          disabled={isLoading}
          name="name"
          control={formMethods.control}
          render={({
            field: { ref: _ref, ...field },
            fieldState: { error },
          }) => (
            <ControlledTextField
              {...field}
              data-testid="new-link-form__name-input"
              label={__("Nazwa")}
              sx={{ width: "100%" }}
              showLimitOnError
              maxLength={150}
              error={!!error}
              helperText={error?.message}
            />
          )}
        />
        <Controller
          disabled={isLoading}
          name="url"
          control={formMethods.control}
          render={({
            field: { ref: _ref, ...field },
            fieldState: { error },
          }) => (
            <ControlledTextField
              {...field}
              data-testid="new-link-form__url-input"
              label={__("Adres URL")}
              sx={{ width: "100%" }}
              error={!!error}
              helperText={error?.message}
            />
          )}
        />
        <Stack
          spacing="1rem"
          data-testid="new-link-form__icon-section"
          sx={{ alignItems: "flex-start", minHeight: "100px" }}
        >
          {cropperImageSrc ? (
            <IconCropper
              ref={iconCropperRef}
              imageSrc={cropperImageSrc}
              onCancel={handleCancelCrop}
              onCropped={handleCropped}
            />
          ) : (
            <>
              {(isLoading || (iconValue && !imgLoaded)) && (
                <Skeleton
                  variant="rectangular"
                  sx={{
                    width: "72px",
                    height: "72px",
                    borderRadius: "1rem",
                  }}
                />
              )}
              {!isLoading && iconValue && (
                <Box
                  sx={{
                    position: "relative",
                    display: imgLoaded ? "block" : "none",
                    paddingBottom: "0.5rem",
                  }}
                >
                  <img
                    style={{ width: "72px", height: "72px" }}
                    src={iconValue.original}
                    alt="icon"
                    data-testid="new-link-form__icon-preview"
                    onLoad={() => setImgLoaded(true)}
                  />
                  <IconActionMenu
                    hasIcon
                    disabled={isLoading}
                    onEdit={() => fileInputRef.current?.click()}
                    onRemove={() => {
                      formMethods.setValue("icon", null);
                      formMethods.setValue("iconId", null);
                      setIconManuallySet(false);
                    }}
                  />
                </Box>
              )}
              {!isLoading && !iconValue && (
                <Box
                  sx={{
                    position: "relative",
                    paddingBottom: "0.5rem",
                    maxWidth: "72px",
                  }}
                >
                  <Box
                    sx={{
                      "& .MuiPaper-root": {
                        maxWidth: "72px",
                        width: "72px",
                        height: "72px",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        margin: "0 0 0.25rem 0",
                        padding: 0,
                      },
                    }}
                  >
                    <DefaultIconWithPaper />
                  </Box>
                  <IconActionMenu
                    hasIcon={false}
                    disabled={isLoading}
                    onEdit={() => fileInputRef.current?.click()}
                    onRemove={() => {
                      formMethods.setValue("icon", null);
                      formMethods.setValue("iconId", null);
                      setIconManuallySet(false);
                    }}
                  />
                </Box>
              )}
            </>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/x-icon,image/gif,image/webp"
            hidden
            onChange={handleFileUpload}
            data-testid="new-link-form__file-input"
          />
          {uploadError && (
            <Typography
              variant="caption"
              color="error"
              data-testid="new-link-form__upload-error"
            >
              {uploadError}
            </Typography>
          )}
        </Stack>
      </FormProvider>
    );
  }
);

NewLinkForm.displayName = "NewLinkForm";

export default NewLinkForm;
